import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../services/supabase'
import { useSimpleHqAccess } from './useSimpleHqAccess'
import { daysAgoIso, formatShortDate, todayIso } from './hqUtils'

type LeaveRow = {
  id: string
  user_id: string
  start_date: string
  end_date: string
  status: string
  days: number
  leave_type_id: string
}

type TaskRow = {
  id: string
  title: string
  status: string
  due_at: string | null
  last_update_at: string | null
  assignee_user_id: string | null
  project_id: string
}

type RosterRow = { user_id: string; email: string; role: string }

export function HqHomeView() {
  const { user } = useAuth()
  const { companyId, isApprover } = useSimpleHqAccess()
  const [pending, setPending] = useState<LeaveRow[]>([])
  const [outSoon, setOutSoon] = useState<LeaveRow[]>([])
  const [myTasks, setMyTasks] = useState<TaskRow[]>([])
  const [stale, setStale] = useState<TaskRow[]>([])
  const [roster, setRoster] = useState<RosterRow[]>([])
  const [types, setTypes] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const today = todayIso()
      const in14 = new Date()
      in14.setDate(in14.getDate() + 14)
      const end = in14.toISOString().slice(0, 10)
      const staleCutoff = daysAgoIso(7)

      const [{ data: leaveTypes }, { data: leaves }, { data: tasks }, { data: rosterData }] =
        await Promise.all([
          supabase.from('leave_types').select('id, name').eq('company_id', companyId),
          supabase
            .from('leave_requests')
            .select('id, user_id, start_date, end_date, status, days, leave_type_id')
            .eq('company_id', companyId)
            .in('status', ['pending', 'approved'])
            .lte('start_date', end)
            .gte('end_date', today),
          supabase
            .from('hq_tasks')
            .select('id, title, status, due_at, last_update_at, assignee_user_id, project_id')
            .eq('company_id', companyId)
            .not('status', 'in', '("done","cancelled")'),
          supabase.rpc('simple_hq_roster'),
        ])

      if (cancelled) return
      const tmap: Record<string, string> = {}
      for (const t of leaveTypes || []) tmap[t.id] = t.name
      setTypes(tmap)
      setRoster((rosterData as RosterRow[]) || [])

      const leaveRows = (leaves as LeaveRow[]) || []
      setPending(leaveRows.filter((l) => l.status === 'pending'))
      setOutSoon(leaveRows.filter((l) => l.status === 'approved'))

      const taskRows = (tasks as TaskRow[]) || []
      setMyTasks(taskRows.filter((t) => t.assignee_user_id === user?.id).slice(0, 8))
      setStale(
        taskRows
          .filter(
            (t) =>
              (t.status === 'active' || t.status === 'waiting') &&
              (!t.last_update_at || t.last_update_at < staleCutoff),
          )
          .slice(0, 8),
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [companyId, user?.id])

  const emailById = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of roster) m[r.user_id] = r.email
    return m
  }, [roster])

  const needsYou = useMemo(() => {
    const items: { key: string; text: string; to: string }[] = []
    if (isApprover) {
      for (const p of pending.slice(0, 5)) {
        items.push({
          key: `leave-${p.id}`,
          text: `Leave: ${emailById[p.user_id]?.split('@')[0] || 'teammate'} · ${formatShortDate(p.start_date)}–${formatShortDate(p.end_date)}`,
          to: '/simple-hq/leave/approvals',
        })
      }
    }
    for (const t of myTasks.filter((x) => x.due_at && x.due_at <= todayIso()).slice(0, 5)) {
      items.push({
        key: `task-${t.id}`,
        text: `Due: ${t.title}`,
        to: `/simple-hq/projects/${t.project_id}`,
      })
    }
    return items
  }, [isApprover, pending, myTasks, emailById])

  if (loading) {
    return <p className="hq-empty">Loading home…</p>
  }

  return (
    <div>
      <p className="hq-eyebrow">Simple HQ</p>
      <h1 className="hq-title">What needs you today</h1>
      <p className="hq-sub">Needs you · Who’s out · My work — leave and projects in one place.</p>

      <div className="hq-grid-3">
        <section className="hq-panel">
          <p className="hq-eyebrow">Needs you</p>
          {needsYou.length === 0 ? (
            <p className="hq-empty">Nothing waiting. Request time off or open My work.</p>
          ) : (
            <ul className="hq-list">
              {needsYou.map((n) => (
                <li key={n.key}>
                  <Link to={n.to} className="font-semibold text-[var(--hq-accent)]">
                    {n.text}
                  </Link>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex gap-2 flex-wrap">
            <Link className="hq-btn hq-btn-primary" to="/simple-hq/leave/request">
              My leave
            </Link>
            {isApprover && (
              <Link className="hq-btn" to="/simple-hq/leave/approvals">
                Approvals ({pending.length})
              </Link>
            )}
          </div>
        </section>

        <section className="hq-panel">
          <p className="hq-eyebrow">Who’s out</p>
          {outSoon.length === 0 ? (
            <p className="hq-empty">No approved leave in the next two weeks.</p>
          ) : (
            <ul className="hq-list">
              {outSoon.slice(0, 8).map((l) => (
                <li key={l.id}>
                  <span className="font-semibold">
                    {emailById[l.user_id]?.split('@')[0] || 'Someone'}
                  </span>
                  <span className="text-[var(--hq-muted)]">
                    {' '}
                    · {types[l.leave_type_id] || 'Leave'} · {formatShortDate(l.start_date)}–
                    {formatShortDate(l.end_date)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <Link className="hq-btn mt-3 inline-flex" to="/simple-hq/leave/calendar">
            Open calendar
          </Link>
        </section>

        <section className="hq-panel">
          <p className="hq-eyebrow">My work</p>
          {myTasks.length === 0 ? (
            <p className="hq-empty">No open tasks assigned to you.</p>
          ) : (
            <ul className="hq-list">
              {myTasks.map((t) => (
                <li key={t.id}>
                  <Link to={`/simple-hq/projects/${t.project_id}`} className="font-semibold">
                    {t.title}
                  </Link>
                  <div className="text-[var(--hq-muted)] text-xs mt-0.5">
                    {t.status}
                    {t.due_at ? ` · due ${formatShortDate(t.due_at)}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <Link className="hq-btn mt-3 inline-flex" to="/simple-hq/work">
            All my work
          </Link>
        </section>
      </div>

      {isApprover && (
        <section className="hq-panel mt-4">
          <p className="hq-eyebrow">Stale pulse</p>
          <p className="hq-sub" style={{ marginBottom: '0.5rem' }}>
            Active/waiting tasks with no progress note in 7+ days.
          </p>
          {stale.length === 0 ? (
            <p className="hq-empty">No stale tasks — nice.</p>
          ) : (
            <ul className="hq-list">
              {stale.map((t) => (
                <li key={t.id}>
                  <Link to={`/simple-hq/projects/${t.project_id}`}>{t.title}</Link>
                  <span className="text-[var(--hq-muted)] text-xs">
                    {' '}
                    · {emailById[t.assignee_user_id || '']?.split('@')[0] || 'unassigned'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  )
}
